import {createFileRoute, Navigate} from "@tanstack/react-router";
import {useMountedTruck} from "../../../driver/mountedTruck.ts";
import {useTruckStatus} from "../../../truck/truck.tsx";
import {useUnfinishedBusiness} from "../../../transit/transitUtils.ts";
import {useMemo} from "react";
import {SuspenseDialog} from "../../../components/SuspenseDialog.tsx";
import {Transfer} from "../../../rpc/webrpc-client.gen.ts";
import {InfallibleUseResult, successUseResult} from "../../../util/hookutil.tsx";

export function useOpenLocalBundleTransfers(): InfallibleUseResult<Transfer[]> {
    const mountedTruckTqrv = useMountedTruck();
    const mountedTruck = mountedTruckTqrv.data;
    const ubrv = useUnfinishedBusiness(mountedTruck ? {truckId: mountedTruck.id} : undefined);
    const rv = useMemo(() => {
        if (mountedTruckTqrv.isSuccess && !mountedTruck) {
            return successUseResult([]);
        } else {
            return ubrv;
        }
    }, [mountedTruck, mountedTruckTqrv.isSuccess, ubrv]);

    return rv;
}

function DriverSwitchboard() {
    const mountedTruckTqrv = useMountedTruck();
    const myTruck = mountedTruckTqrv.data;
    const truckStatusTqrv
        = useTruckStatus(myTruck ? {truckId: myTruck.id} : undefined);

    if (mountedTruckTqrv.isPending || (!!myTruck && truckStatusTqrv.isPending)) {
        return <SuspenseDialog message="my truck status"/>;
    }

    const truckStatus = truckStatusTqrv.data;
    const lastTripleg = truckStatus?.lastTripleg;

    if (!lastTripleg || !myTruck) {
        return <Navigate to="/driver/schedule" replace={true}/>
    }

    if (!lastTripleg.admitted) {
        return <Navigate to="/truck/$id/drive" params={{id: myTruck.id}} replace={true}/>
    }

    if (truckStatus.canDropoff.length > 0) {
        return <Navigate to="/venue/$id/service" params={{id: lastTripleg.destination.id}} replace={true}/>
    }

    // we may have things on board that want to leave here
    // we may have other places we want to go pick up first
    // currently choose to prioritize the dropoffs that are already
    // on board, on the assumption that this is the most common case.
    // both the local pickup start onloading and the departure for
    // the remote dropoff will come from the truck cargo page.
    // however, if there is only a pickup and no potential dropoffs,
    // then we want to go to the venue service page
    if (truckStatus.canPickup.length > 0) {
        if (truckStatus.remainingDropoffs.length > 0) {
            return <Navigate to="/truck/$id/cargo" params={{id: myTruck.id}}
                             replace={true}/>
        } else {
            return <Navigate to="/venue/$id/service" params={{id: lastTripleg.destination.id}}
                             replace={true}/>
        }
    }

    // if there is nothing to drop off, and nothing to pick up here,
    // then all we have left is the schedule
    // 2025.07.19 not quite. there is also the case where we have things on board
    // that want to go home, in which event we should go to the truck cargo page.
    if (!truckStatus.isEmpty) {
        return <Navigate to="/truck/$id/cargo" params={{id: myTruck.id}}
                         replace={true}/>
    }

    return <Navigate to="/driver/schedule" replace={true}/>
}

export const Route = createFileRoute("/_auth/driver/next")({
    component: DriverSwitchboard,
});
